import { Wrench, RefreshCw } from 'lucide-react';
import * as React from 'react';

import { Button } from '~/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '~/components/ui/card';

export function SetupCheck({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = React.useState<
    'checking' | 'ready' | 'setup_required' | 'error'
  >('checking');
  const [errorMessage, setErrorMessage] = React.useState('');

  const checkHealth = React.useCallback(async () => {
    setStatus('checking');
    try {
      const res = await fetch('/api/health', { credentials: 'include' });
      if (res.status === 418) {
        setStatus('setup_required');
      } else if (res.ok) {
        setStatus('ready');
      } else {
        setStatus('error');
        setErrorMessage(`Server returned ${res.status}`);
      }
    } catch (err) {
      setStatus('error');
      setErrorMessage(
        err instanceof Error ? err.message : 'Cannot reach server'
      );
    }
  }, []);

  React.useEffect(() => {
    void checkHealth();
  }, [checkHealth]);

  if (status === 'checking') {
    return (
      <div className="flex h-full min-h-0 items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-muted-foreground">Checking app setup...</p>
        </div>
      </div>
    );
  }

  if (status === 'setup_required') {
    return (
      <div className="flex h-full min-h-0 items-center justify-center overflow-y-auto bg-background p-4">
        <Card className="w-full max-w-lg">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Wrench className="h-5 w-5 text-primary" />
              <CardTitle>Application Not Ready</CardTitle>
            </div>
            <CardDescription>
              This instance is still being configured. Please contact your
              administrator.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              If you are the operator, complete the following setup steps:
            </p>
            <ol className="list-decimal space-y-2 pl-5 text-sm">
              <li>
                Run database migrations (local Wrangler D1):{' '}
                <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                  pnpm db:migrate:local
                </code>
              </li>
              <li>
                Start the API server:{' '}
                <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                  cd apps/api && pnpm dev
                </code>
              </li>
              <li>
                Start the web app:{' '}
                <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                  cd apps/web && pnpm dev
                </code>
              </li>
              <li>
                Authenticate via CLI:{' '}
                <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                  dataroom login
                </code>
              </li>
            </ol>
            <Button onClick={checkHealth} className="w-full">
              <RefreshCw className="mr-2 h-4 w-4" />
              Check Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="flex h-full min-h-0 items-center justify-center overflow-y-auto bg-background p-4">
        <Card className="w-full max-w-lg">
          <CardHeader>
            <CardTitle>Cannot Reach Server</CardTitle>
            <CardDescription>{errorMessage}</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Make sure the API server is running on port 5151 and the web dev
              server proxy is configured correctly.
            </p>
            <Button onClick={checkHealth} className="mt-4 w-full">
              <RefreshCw className="mr-2 h-4 w-4" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}
